import { useEffect, useRef, useState, useCallback } from 'react';

export type ExchangeEventType =
  | 'exchange.started'
  | 'exchange.thinking'
  | 'exchange.step.started'
  | 'exchange.step.completed'
  | 'exchange.task.started'
  | 'exchange.completed'
  | 'exchange.failed'
  | 'conversation.result';

export interface ExchangeEvent {
  type: ExchangeEventType;
  sessionId?: string;
  agentName?: string;
  data?: Record<string, any>;
  timestamp?: string;
}

export interface UseWebSocketOptions {
  url: string;
  token: string | null;
  onEvent: (event: ExchangeEvent) => void;
  enabled?: boolean;
}

export interface UseWebSocketReturn {
  isConnected: boolean;
  subscribe: (sessionId: string, agentName?: string) => void;
  unsubscribe: () => void;
  connectionError: string | null;
}

const MAX_RECONNECT_DELAY = 30000;
const INITIAL_RECONNECT_DELAY = 2000;

export function useWebSocket({
  url,
  token,
  onEvent,
  enabled = true,
}: UseWebSocketOptions): UseWebSocketReturn {
  const [isConnected, setIsConnected] = useState(false);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectDelayRef = useRef(INITIAL_RECONNECT_DELAY);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const onEventRef = useRef(onEvent);
  const subscriptionRef = useRef<{ sessionId: string; agentName?: string } | null>(null);
  const disposedRef = useRef(false);

  onEventRef.current = onEvent;

  useEffect(() => {
    disposedRef.current = false;

    if (!enabled || !token || !url) {
      return;
    }

    let currentWs: WebSocket | null = null;

    const openConnection = () => {
      if (disposedRef.current) return;

      // Close any lingering connection before opening a new one
      if (wsRef.current) {
        wsRef.current.onopen = null;
        wsRef.current.onclose = null;
        wsRef.current.onerror = null;
        wsRef.current.onmessage = null;
        wsRef.current.close();
        wsRef.current = null;
      }

      try {
        const wsUrl = `${url}?token=${encodeURIComponent(token)}`;
        const ws = new WebSocket(wsUrl);
        currentWs = ws;
        wsRef.current = ws;

        ws.onopen = () => {
          if (disposedRef.current || currentWs !== ws) return;
          setIsConnected(true);
          setConnectionError(null);
          reconnectDelayRef.current = INITIAL_RECONNECT_DELAY;

          if (subscriptionRef.current) {
            const msg: Record<string, string> = {
              action: 'subscribe',
              session_id: subscriptionRef.current.sessionId,
            };
            if (subscriptionRef.current.agentName) {
              msg.agent_name = subscriptionRef.current.agentName;
            }
            ws.send(JSON.stringify(msg));
          }
        };

        ws.onmessage = (evt) => {
          if (disposedRef.current) return;
          try {
            const raw = JSON.parse(evt.data);
            const parsed: ExchangeEvent = {
              ...raw,
              type: raw.type || raw.event_type,
              sessionId: raw.sessionId || raw.session_id,
              agentName: raw.agentName || raw.agent_name,
            };
            onEventRef.current(parsed);
          } catch {
            // ignore malformed messages
          }
        };

        ws.onerror = () => {
          if (disposedRef.current) return;
          setConnectionError('WebSocket connection error');
        };

        ws.onclose = () => {
          if (disposedRef.current || currentWs !== ws) return;
          setIsConnected(false);
          wsRef.current = null;

          reconnectTimerRef.current = setTimeout(() => {
            reconnectDelayRef.current = Math.min(
              reconnectDelayRef.current * 2,
              MAX_RECONNECT_DELAY,
            );
            openConnection();
          }, reconnectDelayRef.current);
        };
      } catch (err) {
        if (!disposedRef.current) {
          setConnectionError(String(err));
        }
      }
    };

    openConnection();

    return () => {
      disposedRef.current = true;
      clearTimeout(reconnectTimerRef.current);
      if (wsRef.current) {
        wsRef.current.onopen = null;
        wsRef.current.onclose = null;
        wsRef.current.onerror = null;
        wsRef.current.onmessage = null;
        wsRef.current.close();
        wsRef.current = null;
      }
      currentWs = null;
      setIsConnected(false);
    };
  }, [enabled, token, url]);

  const subscribe = useCallback((sessionId: string, agentName?: string) => {
    subscriptionRef.current = { sessionId, agentName };
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      const msg: Record<string, string> = {
        action: 'subscribe',
        session_id: sessionId,
      };
      if (agentName) msg.agent_name = agentName;
      ws.send(JSON.stringify(msg));
    }
  }, []);

  const unsubscribe = useCallback(() => {
    subscriptionRef.current = null;
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ action: 'unsubscribe' }));
    }
  }, []);

  return { isConnected, subscribe, unsubscribe, connectionError };
}
